import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useSmartNav } from "@/lib/scroll";

const LINKS = [
    { label: "About", target: "#about" },
    { label: "Capabilities", target: "#capabilities" },
    { label: "AI", target: "#ai" },
    { label: "Services", target: "/services" },
    { label: "Partners", target: "/partners" },
    { label: "Product", target: "/products/pqc-readiness" },
    { label: "Careers", target: "/careers" },
    { label: "FAQ", target: "#faq" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const go = useSmartNav();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleGo = (target) => {
        setOpen(false);
        go(target);
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "border-b border-white/[0.08] bg-[#07090E]/80 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
                <button onClick={() => handleGo("#hero")} aria-label="Syntera Technologies home" className="cursor-pointer" data-testid="nav-logo-btn">
                    <Logo />
                </button>

                <div className="hidden items-center gap-6 xl:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.target}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => handleGo(l.target)}
                            className="group relative font-mono text-xs uppercase tracking-[0.14em] text-slate-400 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </div>

                <div className="hidden xl:block">
                    <button
                        data-testid="nav-book-call-btn"
                        onClick={() => handleGo("#contact")}
                        className="group flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]"
                    >
                        Book a Call
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </div>

                <button
                    data-testid="nav-mobile-menu-btn"
                    className="text-white xl:hidden"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-panel"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-b border-white/[0.08] bg-[#07090E]/95 backdrop-blur-xl xl:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.target}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => handleGo(l.target)}
                                    className="rounded-lg px-3 py-3 text-left font-mono text-sm uppercase tracking-[0.15em] text-slate-300 transition-colors hover:bg-white/5 hover:text-volt"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-book-call-btn"
                                onClick={() => handleGo("#contact")}
                                className="mt-2 rounded-full bg-volt px-5 py-3 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#07090E]"
                            >
                                Book a Call
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
